import React from 'react';
import { Link } from 'react-router-dom';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-white to-primary-50 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-900 transition-colors duration-300">
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 dark:bg-indigo-500 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-xl opacity-70 dark:opacity-30 animate-pulse-soft"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-200 dark:bg-purple-500 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-xl opacity-70 dark:opacity-30 animate-pulse-soft animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-success-100 dark:bg-blue-600 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-xl opacity-50 dark:opacity-20 animate-pulse-soft animation-delay-4000"></div>
      </div>

      <div className="relative z-10 min-h-screen">
        {/* Navigation Bar */}
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Back to Dashboard</span>
          </Link>
        </div>

        <div className="max-w-4xl mx-auto px-6 pb-12">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
              🔐 Privacy Policy for Data Bing Bong
            </h1>
            <div className="space-y-2 text-slate-600 dark:text-slate-300">
              <p><strong>Effective Date:</strong> July 22, 2025</p>
              <p><strong>Last Updated:</strong> July 22, 2025</p>
            </div>
            <p className="mt-6 text-lg text-slate-700 dark:text-slate-200 max-w-3xl mx-auto">
              At Data Bing Bong, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect information through our internal Amazon Selling Partner API application, BingBong.
            </p>
          </div>

          {/* Content sections */}
          <div className="space-y-12">
            {/* Information We Collect */}
            <section className="bg-white dark:bg-slate-800/50 rounded-lg p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-6 flex items-center">
                📘 Information We Collect
              </h2>
              <div className="space-y-4 text-slate-700 dark:text-slate-300">
                <p>We use the Amazon Selling Partner API (SP-API) to access data related only to our own Amazon seller account. This may include:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Sales and performance metrics</li>
                  <li>Product listing data</li>
                  <li>Brand analytics and insights</li>
                </ul>
                <p className="font-medium text-slate-800 dark:text-white">
                  We do not collect or store any customer Personally Identifiable Information (PII).
                </p>
              </div>
            </section>

            {/* How We Use This Information */}
            <section className="bg-white dark:bg-slate-800/50 rounded-lg p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-6 flex items-center">
                🔐 How We Use This Information
              </h2>
              <div className="space-y-4 text-slate-700 dark:text-slate-300">
                <p>We use the data collected solely to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Analyze sales and operational performance</li>
                  <li>Optimize product listings and advertising</li>
                  <li>Improve internal business decision-making</li>
                </ul>
                <p className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg">
                  All data usage complies with Amazon's SP-API Acceptable Use Policy and Data Protection Policy.
                </p>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="bg-white dark:bg-slate-800/50 rounded-lg p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-6 flex items-center">
                🧷 Data Sharing
              </h2>
              <p className="text-slate-700 dark:text-slate-300 font-medium">
                We do not share, sell, rent, or disclose any data to third parties.
              </p>
            </section>

            {/* Data Security */}
            <section className="bg-white dark:bg-slate-800/50 rounded-lg p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-6 flex items-center">
                🛡️ Data Security
              </h2>
              <div className="space-y-4 text-slate-700 dark:text-slate-300">
                <p>We use industry-standard security practices to protect your data, including:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Secure credential storage via AWS Secrets Manager</li>
                  <li>Limited IAM role access and least-privilege permissions</li>
                  <li>Logging and monitoring via AWS CloudWatch</li>
                </ul>
              </div>
            </section>

            {/* Changes to This Policy */}
            <section className="bg-white dark:bg-slate-800/50 rounded-lg p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-6 flex items-center">
                🔁 Changes to This Policy
              </h2>
              <p className="text-slate-700 dark:text-slate-300">
                We may update this policy occasionally. If we make changes, we will update the effective date at the top of this page.
              </p>
            </section>

            {/* Contact Us */}
            <section className="bg-white dark:bg-slate-800/50 rounded-lg p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-6 flex items-center">
                📫 Contact Us
              </h2>
              <div className="space-y-4 text-slate-700 dark:text-slate-300">
                <p>If you have any questions about this policy or your data, contact:</p>
                <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg space-y-2">
                  <p><strong>Email:</strong> <a href="mailto:hello@databingbong.com" className="text-primary-600 dark:text-primary-400 hover:underline">hello@databingbong.com</a></p>
                  <p><strong>Business Name:</strong> Data Bing Bong LLC</p>
                  <p><strong>Website:</strong> <a href="https://databingbong.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">https://databingbong.com</a></p>
                </div>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center space-x-2 text-sm text-secondary-500 dark:text-slate-400">
              <div className="w-2 h-2 bg-success-400 dark:bg-emerald-400 rounded-full animate-pulse-soft"></div>
              <span>Privacy Policy</span>
              <span>•</span>
              <span>Data Bing Bong</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy; 